import type { RecoilState, TransactionInterface_UNSTABLE } from "recoil"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { cookieCut } from "~/utils/cookieCut"

import type {
  UnknownActionForUnknownElement,
  UnknownChangeForUnknownElement,
  UnknownElementChangeTo_,
} from "."
import { findChangeBatchesState, findMarkerState } from "."
import type { Elements } from ".."
import {
  getElementRelations1ToMany,
  getElementFamilies,
  getElementIndices,
} from ".."
import { INVERSE_ACTION_TYPE } from "./utils"
import { areaSelectionState } from "../areas"
import { edgeSelectionState } from "../edges"
import { nodeSelectionState_INTERNAL } from "../nodeSelection"
import { panelSelectionState } from "../panels"
import type { TransactionOperation } from "../transactions"

export const generateElementChange = <
  ACTION extends UnknownActionForUnknownElement,
>(
  { get }: TransactionInterface_UNSTABLE,
  { action, from = `redo` }: { action: ACTION; from?: `redo` | `undo` },
): UnknownChangeForUnknownElement => {
  const to = from === `redo` ? `undo` : `redo`
  const { type, family, id } = action
  const label = `${
    from === `redo` ? type : INVERSE_ACTION_TYPE[type]
  } ${family} ${id}${
    action.type === `ATTACH` || action.type === `DETACH`
      ? ` to ${action.relatedId}`
      : ``
  }`
  const actionCore = {
    family,
    id,
  }
  switch (action.type) {
    case `CREATE`:
      return {
        label,
        [from]: action,
        [to]: {
          type: `DELETE`,
          ...actionCore,
        },
      } as UnknownChangeForUnknownElement
    case `DELETE`:
      return {
        label,
        [from]: action,
        [to]: {
          type: `CREATE`,
          ...actionCore,
        },
      } as UnknownChangeForUnknownElement
    case `UPDATE`:
      return {
        label,
        [from]: action,
        [to]: {
          type: `UPDATE`,
          family,
          id,
          payload: cookieCut(
            get(
              getElementFamilies()[family as keyof Elements](
                id,
              ) as RecoilState<unknown>,
            ),
            action.payload,
          ) as UnknownElementChangeTo_[`UPDATE`][`undo`][`payload`],
        },
      } as UnknownChangeForUnknownElement
    case `ATTACH`:
      return {
        label,
        [from]: action,
        [to]: {
          type: `DETACH`,
          family,
          id,
          relatedId: action.relatedId,
        },
      } as UnknownChangeForUnknownElement
    case `DETACH`:
      return {
        label,
        [from]: action,
        [to]: {
          type: `ATTACH`,
          family,
          id,
          relatedId: action.relatedId,
        },
      } as UnknownChangeForUnknownElement
    default:
      throw new Error(`Unknown action type ${type}`)
  }
}

export const divergeTimeline: TransactionOperation<
  (UnknownChangeForUnknownElement | void)[]
> = ({ get, set, reset }, changes) => {
  const { phase } = get(designLocalState)
  const markerState = findMarkerState(phase)
  const marker = get(markerState)
  const actualChanges = changes.filter(
    Boolean,
  ) as UnknownChangeForUnknownElement[]
  set(findChangeBatchesState(phase), (changeBatches) => {
    const newChangeBatches = changeBatches.slice(marker + 1)

    return [actualChanges, ...newChangeBatches]
  })
  reset(markerState)
}

export type TimeTravelCommand = `redo` | `undo`
export type TimeTravel = (command: TimeTravelCommand) => void
export const useTimeTravel = (): TimeTravel =>
  useRecoilTransaction_UNSTABLE(
    ({ get, set, reset }) =>
      (command: TimeTravelCommand) => {
        const SELECTIONS: Record<keyof Elements, Set<string>> = {
          NODE: new Set(),
          EDGE: new Set(),
          AREA: new Set(),
          PANEL: new Set(),
        }
        const { phase } = get(designLocalState)
        const changeBatchesState = findChangeBatchesState(phase)
        const batches = get(changeBatchesState)
        const markerState = findMarkerState(phase)
        const marker = get(markerState)
        const newMarker = command === `undo` ? marker + 1 : marker - 1
        if (newMarker < -1 || newMarker >= batches.length) return
        const changes = batches[command === `undo` ? newMarker : newMarker + 1]
        changes.forEach((change) => {
          const action = change[command]
          switch (action.type) {
            case `CREATE`:
            case `DELETE`:
            case `UPDATE`:
              /* eslint-disable no-case-declarations */
              const index = getElementIndices()[action.family]
              const findElement = getElementFamilies()[action.family]
              switch (action.type) {
                case `CREATE`:
                  set(index, (ids) => [...ids, action.id])
                  break
                case `DELETE`:
                  set(index, (ids) => ids.filter((id) => id !== action.id))
                  break
                case `UPDATE`:
                  // @ts-expect-error ts gets confused unless you repeat this exact logic
                  // in a discriminator for each union member, which is silly and wasteful.
                  set(findElement(action.id), (element) => ({
                    ...element,
                    ...action.payload,
                  }))
              }
              if (action.type !== `DELETE`) {
                SELECTIONS[action.family].add(action.id)
              }
              break
            case `ATTACH`:
            case `DETACH`:
              if (action.family === `AREA_PANEL`) {
                const relationState = getElementRelations1ToMany()[action.family]
                const { id, relatedId } = action
                set(relationState, (current) =>
                  action.type === `ATTACH`
                    ? current.set(id, relatedId)
                    : current.delete(id, relatedId),
                )
              }
              break
          }
        })
        set(nodeSelectionState_INTERNAL, SELECTIONS.NODE)
        set(edgeSelectionState, SELECTIONS.EDGE)
        set(areaSelectionState, SELECTIONS.AREA)
        set(panelSelectionState, SELECTIONS.PANEL)
        set(markerState, newMarker)
      },
  )

export const useUndoRedo = (): [VoidFunction, VoidFunction] => {
  const timeTravel = useTimeTravel()
  const undo = () => timeTravel(`undo`)
  const redo = () => timeTravel(`redo`)

  return [undo, redo]
}
